import { getInventoryBuyerApprovalStatus, getInventoryBuyerReleaseStatus } from "@foresting-tomorrow/converters";
import { useEffect, useMemo, useRef, useState } from "react";
import dayjs from "dayjs";
import { cloneDeep } from "lodash";
import { getCountryData, TCountryCode } from "countries-list";
import { getLogVolumesFromWeightedDiameter } from "@foresting-tomorrow/forest_tools_inventory";

// MUI
import { Tab, Tabs } from "@mui/material";

// Project import
import SingleColumnInput from "components/input/singleColumnInput";
import useInventoryData from "hooks/useInventoryData";
import RawWoodInput from "./rawWoodInput";
import { useTranslation } from "utils/locales/utilityFunctions";
import ConfirmDialog from "components/dialogs/ConfirmDialog";
import {
  BuyerStatusChangeProps,
  FirestoreInventoryItemProps,
  FirestoreInventoryMeasurementProps,
  SaveInventoryItemProps,
  SaveInventoryMeasurementProps,
  TeamMember,
} from "types/auth";
import getActivityOptions, {
  ActivityOption,
} from "components/estatesAndMapSelecter/getActivityOptions";
import useMapData from "hooks/useMapData";
import useEstatesAndMaps from "hooks/useEstatesAndMaps";
import useAuth from "hooks/useAuth";
import getContactOptions from "components/estatesAndMapSelecter/getContactOptions";
import { ContactType } from "components/shareData/shareDialog";
import {
  RawWoodInputInformationSpeciesHandle,
  RawWoodSpeciesItem,
} from "./rawWoodInputInformationSpecies";
import useThinningData from "hooks/useThinningData";
import { certificationTagsOptions, FirestoreActivityProps } from "types/firestoreFeatures";
import useSpeciesData from "hooks/useSpeciesData";
import { beautifyNumber, parseLocalizedNumber, strInputNumberCorrection } from "utils/numberModification";
import useConfig from "hooks/useConfig";
import {
  ASSORTMENT_OPTIONS,
  STACKING_OPTIONS,
  PROCESSING_OPTIONS,
  STRAIGHTNESS_OPTIONS,
  SNOW_ICE_OPTIONS,
  RESIDUES_OPTIONS,
  REJECTS_OPTIONS,
  BATTENS_OPTIONS,
  STATUS_OPTIONS,
  ACCESSIBILITY_OPTIONS,
  BUYER_APPROVAL_OPTIONS,
  WOOD_LOG_ASSORTMENT_OPTIONS,
  LOG_CLASS_OPTIONS,
  LOG_CLASS_EXPORT_OPTIONS,
} from "./options";
import useActivityData from "hooks/useActivityData";
import { LogEntry, RawWoodInputMeasurementLogsHandler } from "./rawWoodInputMeasurementLogs";
import A11yProps from "components/navigation/a11yProps";
import useAppFunctionalityConfig from "hooks/useAppFunctionalityConfig";
import { speciesChanged, logRowsChanged } from "./utils/detectChanges";
import { enqueueSnackbar } from "notistack";
import useFiles from "hooks/useFiles";
import { FirestoreFileProps, UploadFileEntryProps } from "types/files";
import React from "react";
import { validateAndInformInventory } from "./utils/validateRawWoodInventory";
import { SnackbarDismissAction } from "components/actions/snackbarActions";
import { DEFAULT_SNACKBAR_DISMISS_TIMEOUT } from "config/config-general";
import config from "config/config";
import { measurementsFromForm, heightsForSave, displayInventoryValue, parseMeasurementNumber, heightMetresToInputCm } from "./utils/calculations";
import { calculateStackGeometry } from "@foresting-tomorrow/forest_tools_inventory";
import { approvalStatus, inventoryLocked } from "utils/inventoryLifecycle";
import { getInventoryDisplayReference } from "utils/inventoryDisplayReference";

type RawWoodInventoryAndMeasurementInputProps = {
  open: boolean;
  selectedInventoryItem: FirestoreInventoryItemProps;
  liveInventory?: FirestoreInventoryItemProps;
  measurementData?: FirestoreInventoryMeasurementProps[]; // Define the type based on your requirements
  handleClose: () => void;
};

const RawWoodInventoryAndMeasurementInput = ({
  open,
  selectedInventoryItem,
  liveInventory,
  measurementData,
  handleClose,
}: RawWoodInventoryAndMeasurementInputProps) => {
  const style = {
    "&.MuiTab-root": {
      "&:hover": {
        bgcolor: "secondary.light",
        height: 20,
      },
    },
    "&.Mui-selected": {
      bgcolor: "primary.lighter",
      "&:hover": {
        bgcolor: "primary.lighter",
        height: 30,
      },
    },
  };

  // --- Hooks --- //
  const { translate } = useTranslation();
  const { orgContacts, orgTeam, user, userOrg, callCloudFunction, buyerOrgsById } = useAuth();
  const { projects, orgProjects, estates } = useEstatesAndMaps();
  const { activities, fetchActivities, saveInventoryEditor } = useMapData();
  const { getParametersFromInventoryType } = useInventoryData();
  const {
    projects: projectFiles,
    inventories: inventoryFiles,
    uploadFiles,
    fetchFiles,
  } = useFiles();
  const { getSpeciesOptions } = useSpeciesData();
  const { getActivityData } = useActivityData();
  const { i18n } = useConfig();
  const AppConfig = useAppFunctionalityConfig().config;
  const [baselineInventory] = useState(() => cloneDeep(selectedInventoryItem));
  const baselineMeasurements = useRef(measurementData ?? []);
  const savedApproval = approvalStatus(liveInventory ?? selectedInventoryItem);
  const persistedLock = inventoryLocked(liveInventory ?? selectedInventoryItem) || inventoryLocked(baselineInventory);
  const isBuyerOrganisation = selectedInventoryItem.buyer?.id === userOrg?.id;
  const canEditStack = !persistedLock && AppConfig.inventoryData.changeData === "enabled";
  const canDecide = ["pending", "approved"].includes(savedApproval) && approvalStatus(baselineInventory) === savedApproval && isBuyerOrganisation;
  const actor = { id: user?.id || "", name: `${user?.firstName || ""} ${user?.lastName || ""}`.trim(), email: user?.email || "" };
  const reportSaveError = () => enqueueSnackbar(translate("inventory-save-conflict"), { variant: "error" });

  // --- Input Parameters --- //
  // Inventory data
  const {
    inventoryType,
    isChipped: isChippedIn,
    certification: certificationIn,
    certificationNumbers: certificationNumbersIn,
    certificationClaims: certificationClaimsIn,
    certificationNumber: certificationNumberIn,
    origins: originsIn,
    fellingDate,
    measurementResponsible,
    loggingCompany,
    removalCompany,
    chippingCompany,
    buyer,
    pricePerUnit,
    inventoryStatus,
    inventoryStatusNote: inventoryStatusNoteIn,
    uniqueInventoryId,
    assortment: inventoryAssortment,
  } = selectedInventoryItem;
  const variant = inventoryType;
  const par = getParametersFromInventoryType(variant);
  const inventoryDisplayReference = getInventoryDisplayReference(selectedInventoryItem);
  const title = `${par.title}  ${inventoryDisplayReference}`;
  let countryCode = "DK";
  if (originsIn?.estateId && estates && estates[originsIn.estateId]) {
    countryCode = estates[originsIn.estateId].country || "DK";
  }

  // Measurement data
  let initData: any = {};
  if (measurementData && measurementData.length > 0) {
    if (variant === "WoodStack" || variant === "WoodChipStack") {
      const {
        species,
        assortment,
        logLengthM,
        averageDiameterM,
        numberOfCullLogs,
        numberOfSupportLogs,
        volumeOfSupportLogsM3,
        volumeOfCullLogsM3,
        frontHeightsM,
        backHeightsM,
        stackLengthFrontM,
        stackLengthBackM,
        stackingParameters,
        manualCorrectionPercentage,
        manualVolumeM3,
      } = measurementData[0];
      // Initialize data for WoodStack
      initData = {
        species: species
          ? species.map((el, i) => ({
              uid: i + 1,
              id: el.id,
              mix: el.mix ? el.mix.toString() : "",
            }))
          : [],
        woodChipSpecies: species && variant === "WoodChipStack" ? species[0].id : "",
        woodChipManualCompFactor: manualCorrectionPercentage
          ? manualCorrectionPercentage.toString().replace(".", ",")
          : "",
        woodChipManualVolume: manualVolumeM3 ? beautifyNumber(manualVolumeM3, 2) : "",
        assortment: inventoryAssortment || assortment || "",
        averageLogLength: logLengthM ? logLengthM.toString().replace(".", ",") : "",
        averageLogDiameter: averageDiameterM ? (averageDiameterM * 100).toString().replace(".", ",") : "",
        numberOfSections: frontHeightsM?.length || "",
        numberOfSectionsBack: backHeightsM?.length || "",
        measureBothSides:
          variant === "WoodChipStack"
            ? false
            : !backHeightsM?.length
            ? false
            : true,
        frontHeights: frontHeightsM ? frontHeightsM.map(heightMetresToInputCm) : [],
        backHeights: backHeightsM ? backHeightsM.map(heightMetresToInputCm) : [],
        stackLengthFront: stackLengthFrontM ? stackLengthFrontM.toString().replace(".", ",") : "",
        stackLengthBack: stackLengthBackM ? stackLengthBackM.toString().replace(".", ",") : "",
        stackingValue:
          stackingParameters && stackingParameters.stackingMethod
            ? stackingParameters.stackingMethod
            : STACKING_OPTIONS[0].id,
        straightnessValue:
          stackingParameters && stackingParameters.logStraightness
            ? stackingParameters.logStraightness
            : STRAIGHTNESS_OPTIONS[0].id,
        processingValue:
          stackingParameters && stackingParameters.processingResult
            ? stackingParameters.processingResult
            : PROCESSING_OPTIONS[0].id,
        snowIceValue:
          stackingParameters && stackingParameters.snowAndIceCoverage
            ? stackingParameters.snowAndIceCoverage
            : SNOW_ICE_OPTIONS[0].id,
        residuesValue:
          stackingParameters && stackingParameters.loggingResidue
            ? stackingParameters.loggingResidue
            : RESIDUES_OPTIONS[0].id,
        battensValue:
          volumeOfSupportLogsM3 && volumeOfSupportLogsM3 > 0
            ? beautifyNumber(volumeOfSupportLogsM3, 2)
            : numberOfSupportLogs
            ? beautifyNumber(numberOfSupportLogs)
            : BATTENS_OPTIONS[0].id,
        rejectsValue:
          volumeOfCullLogsM3 && volumeOfCullLogsM3 > 0
            ? beautifyNumber(volumeOfCullLogsM3, 2)
            : numberOfCullLogs
            ? beautifyNumber(numberOfCullLogs)
            : REJECTS_OPTIONS[0].id,
        inputModeBattens: volumeOfSupportLogsM3 && volumeOfSupportLogsM3 > 0 ? "volume" : "count",
        inputModeRejects: volumeOfCullLogsM3 && volumeOfCullLogsM3 > 0 ? "volume" : "count",
      };
    } else if (variant === "WoodLogStack") {
      let newLogRows: LogEntry[] = [];
      measurementData.forEach((data) => {
        const {
          species,
          assortment,
          classification,
          logLengthM,
          averageDiameterM,
          averageDiameter2M,
        } = data;
        if (!initData.assortment && assortment) initData.assortment = assortment;
        // Get species
        const spc = species && species.length > 0 ? species[0].id : "";
        // Calculate volume
        let volume = 0;
        if (averageDiameterM && logLengthM) {
          let diameter = averageDiameterM;
          if (averageDiameter2M) {
            diameter = (averageDiameterM + averageDiameter2M) / 2; // Average of both diameters
          }
          // Convert to cm
          const diameterCm = diameter * 100; // Convert to cm
          const volumes = getLogVolumesFromWeightedDiameter({
            logLengthM: logLengthM,
            logMidtDiameterCm: diameterCm,
            species: spc,
          });
          volume = volumes ? volumes.overbarkVolume : 0;
        }
        // Insert data into new log rows
        newLogRows.push({
          id: data.id,
          uniqueMeasurementId: data.uniqueMeasurementId,
          uniqueIdForRelease: null,
          species: spc,
          length: logLengthM ? beautifyNumber(logLengthM, 2) : "",
          diameter: averageDiameterM ? beautifyNumber(averageDiameterM * 100, 2) : "",
          diameter2: averageDiameter2M ? beautifyNumber(averageDiameter2M * 100, 2) : "",
          volume: volume ? beautifyNumber(volume, 2) : "",
          qClass: classification ? classification : "",
          loading: false,
          error: null,
        });
      });
      initData.logRows = newLogRows;
    }
  }

  // --- Data --- //
  const assortmentOptions = useMemo(() => {
    return ASSORTMENT_OPTIONS.map((option) => ({
      id: option,
      label: translate(`raw-wood-assortment-${option}`),
    })).sort((a, b) => a.label.localeCompare(b.label));
  }, [translate("render-change")]);

  const woodLogAssortmentOptions = useMemo(() => {
    return WOOD_LOG_ASSORTMENT_OPTIONS.map((option) => ({
      id: option,
      label: translate(`raw-wood-assortment-${option}`),
    })).sort((a, b) => a.label.localeCompare(b.label));
  }, [translate("render-change")]);

  const woodLogClassOptions = useMemo(() => {
    return LOG_CLASS_OPTIONS.map((option) => ({
      id: option,
      label: translate(`raw-wood-log-class-option-${option}`),
    })).sort((a, b) => a.label.localeCompare(b.label));
  }, [translate("render-change")]);

  const woodLogClassExportOptions = useMemo(() => {
    return LOG_CLASS_EXPORT_OPTIONS.map((option) => ({
      id: option,
      label: translate(`raw-wood-log-class-option-${option}`),
    }));
  }, [translate("render-change")]);

  const stackingOptions = useMemo(() => {
    return STACKING_OPTIONS.map((option) => ({
      ...option,
      label: translate(option.label) + ": " + option.adjustment,
    }));
  }, [translate("render-change")]);

  const processingOptions = useMemo(() => {
    return PROCESSING_OPTIONS.map((option) => ({
      ...option,
      label: translate(option.label) + ": " + option.adjustment,
    }));
  }, [translate("render-change")]);

  const straightnessOptions = useMemo(() => {
    return STRAIGHTNESS_OPTIONS.map((option) => ({
      ...option,
      label: translate(option.label) + ": " + option.adjustment,
    }));
  }, [translate("render-change")]);

  const snowIceOptions = useMemo(() => {
    return SNOW_ICE_OPTIONS.map((option) => ({
      ...option,
      label: translate(option.label) + ": " + option.adjustment,
    }));
  }, [translate("render-change")]);

  const residuesOptions = useMemo(() => {
    return RESIDUES_OPTIONS.map((option) => ({
      ...option,
      label: translate(option.label) + ": " + option.adjustment,
    }));
  }, [translate("render-change")]);

  const rejectsOptions = useMemo(() => {
    return REJECTS_OPTIONS.map((option) => ({
      ...option,
    }));
  }, [translate("render-change")]);

  const battensOptions = useMemo(() => {
    return BATTENS_OPTIONS.map((option) => ({
      ...option,
    }));
  }, [translate("render-change")]);

  const statusOptions = useMemo(() => {
    return STATUS_OPTIONS.map((option) => ({
      id: option,
      label: translate(`raw-wood-input-handling-status-${option}`),
    }));
  }, [translate("render-change")]);

  const accessibilityOptions = useMemo(() => {
    return ACCESSIBILITY_OPTIONS.map((option) => ({
      id: option,
      label: translate(`raw-wood-input-handling-accessibility-${option}`),
    }));
  }, [translate("render-change")]);

  const projectOptions = useMemo(() => {
    const relevantProjects = Object.values(projects).filter(
      (project) => project.estateId === originsIn?.estateId && project.projectStatus !== "archived",
    );
    // Keep track of project IDs that are already in the list, so we don't duplicate them
    const relProjIds = relevantProjects.map((project) => project.id);
    const relevantOrgProjects = Object.values(orgProjects).filter((project) => {
      return project.estateId === originsIn?.estateId && !relProjIds.includes(project.id);
    });
    // Fetch activities for relevant projects
    relevantProjects.forEach((project) => {
      if (!activities[project.id]) {
        fetchActivities(project.id);
      }
    });
    relevantOrgProjects.forEach((project) => {
      if (!activities[project.id]) {
        fetchActivities(project.id, "admin");
      }
    });
    const allProjects = [...relevantProjects, ...relevantOrgProjects];
    return allProjects;
  }, [projects, orgProjects]);

  const responsibleOptions = useMemo(() => {
    const team = orgTeam
      ? Object.entries(orgTeam).map(([id, el]: [string, TeamMember]) => {
          const name = el.name && el.name !== "" ? el.name : el.email;
          return {
            ...el,
            id: id,
            name: name,
            label: name + ":" + el.email,
            personType: "team" as any,
            personTypeLabelText: translate("team"),
          };
        })
      : [];
    const contacts = getContactOptions({
      userId: user ? user.id! : "",
      contacts: orgContacts ? orgContacts : {},
      selectedData: null,
      orgMapIds: [],
      orgProjectIds: [],
      personTypeLabelText: translate("contacts"),
      removeUserNotCreated: true,
    });
    const teamList = team ? (team as ContactType[]) : [];
    const contactsList = contacts ? (contacts as ContactType[]) : [];
    return [...teamList, ...contactsList];
  }, [orgTeam, orgContacts]);

  // --- State and references --- //
  const [dataChanged, setDataChanged] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [loading, setLoading] = useState(false);

  const [certification, setCertification] = useState<string[]>([...(certificationIn || [])]);
  const [certificationNumbers, setCertificationNumbers] = useState<
    NonNullable<FirestoreInventoryItemProps["certificationNumbers"]>
  >({ ...(certificationNumbersIn || {}) });
  const [certificationClaims, setCertificationClaims] = useState<
    NonNullable<FirestoreInventoryItemProps["certificationClaims"]>
  >({ ...(certificationClaimsIn || {}) });
  const certificationNumber = certificationNumberIn || "";
  const [origins, setOrigins] = useState<ActivityOption[]>([]);
  const [responsible, setResponsible] = useState<ContactType[]>(() => {
    if (measurementResponsible && measurementResponsible.id) {
      const responsibleData = responsibleOptions.find(
        (option) => option.id === measurementResponsible.id,
      );
      if (responsibleData) {
        return [responsibleData];
      }
    }
    // Else find current user in responsible options
    const currentUser = responsibleOptions.find((option) => option.id === user?.id);
    return currentUser ? [currentUser] : [];
  });
  const [date, setDate] = useState<Date | null>(
    fellingDate ? dayjs(fellingDate).toDate() : dayjs().toDate(),
  );

  const handlingCompaniesOptions = useMemo(() => {
    let contacts: ContactType[] | null = null;
    contacts = getContactOptions({
      userId: user ? user.id! : "",
      contacts: orgContacts ? orgContacts : {},
      selectedData: null,
      orgMapIds: [],
      orgProjectIds: [],
      personTypeLabelText: translate("contacts"),
      removeUserNotCreated: true,
    });
    if (!contacts) contacts = [];
    const contactOptions = contacts;
    // Add user organization as option in the top
    const sortedContacts = contactOptions.sort((a, b) => {
      const aName = a.orgName || "";
      const bName = b.orgName || "";
      return aName.localeCompare(bName);
    });
    // Add org Team members as options
    if (orgTeam && userOrg) {
      Object.entries(orgTeam).forEach(([id, el]: [string, TeamMember]) => {
        sortedContacts.unshift({
          id: id,
          label: `${el.name}:${el.email}`,
          orgId: userOrg.id ? userOrg.id : "",
          email: el.email,
          orgName: userOrg.name ? userOrg.name : "",
          nameAndOrgName: `${el.name} (${userOrg.name})`,
        });
      });
    }
    return sortedContacts;
  }, [orgContacts, orgTeam, user, userOrg, translate("render-change")]);

  const buyerCompanyOptions = useMemo(() => {
    // Check if buyer is the same as userOrg. If so only return that option
    // if (buyer && buyer.id === userOrg?.id) {
    //   // Set nameAndOrgName for buyer option
    //   let nameAndOrgName = buyer.name;
    //   if (user && user.id === buyer.userId && user.name) {
    //     nameAndOrgName = `${user.name} (${buyer.name})`;
    //   }
    //   return [
    //     {
    //       id: buyer.id,
    //       label: `${buyer.id}:${buyer.name}`,
    //       orgName: buyer.name,
    //       nameAndOrgName: nameAndOrgName,
    //     },
    //   ];
    // }

    // Else return all handling companies except userOrg
    const filteredOptions = handlingCompaniesOptions.filter((company) => {
      // Exclude the user's own organization if disabled in config
      if (
        AppConfig.inventoryData.selfBuyer !== "enabled" &&
        userOrg &&
        company.orgId === userOrg.id
      ) {
        return false;
      }
      return true;
    });
    return filteredOptions;
  }, [AppConfig.inventoryData.selfBuyer, handlingCompaniesOptions, userOrg]);

  // A locked configuration assigns the sole available buyer contact.
  const buyerSelectionLocked = AppConfig.inventoryData.buyerSelectionLocked === true;
  const lockedBuyerOption =
    buyerSelectionLocked && buyerCompanyOptions.length === 1 ? buyerCompanyOptions[0] : undefined;

  const speciesOptions = getSpeciesOptions({ withoutHeaders: true });

  const woodLogSpeciesOptions = speciesOptions;

  // --- State & Refs --- //
  const [assortment, setAssortment] = useState<string>(() => {
    const testAssortment = inventoryAssortment || initData.assortment || "";
    if (testAssortment) {
      const option = assortmentOptions.find((el) => el.id === testAssortment);
      if (option) {
        return option.id;
      }
    }
    return "";
  });
  const [averageLogLength, setAverageLogLength] = useState<string>(initData.averageLogLength || "");
  const [isChipped, setIsChipped] = useState<boolean>(isChippedIn ?? false);
  const [averageLogDiameter, setAverageLogDiameter] = useState<string>(
    initData.averageLogDiameter || "",
  );

  // General states
  const [numberOfSections, setNumberOfSections] = useState<number | "">(
    initData.numberOfSections || "",
  );
  const [numberOfSectionsBack, setNumberOfSectionsBack] = useState<number | "">(initData.numberOfSectionsBack || "");
  const [measureBothSides, setMeasureBothSides] = useState<boolean>(
    variant === "WoodChipStack"
      ? false
      : initData.measureBothSides === true || initData.measureBothSides === undefined
      ? true
      : false,
  );
  const [stackLengthFront, setStackLengthFront] = useState<string>(initData.stackLengthFront || "");
  const [stackLengthBack, setStackLengthBack] = useState<string>(initData.stackLengthBack || "");
  const [frontHeights, setFrontHeights] = useState<string[]>(initData.frontHeights || []);
  const [backHeights, setBackHeights] = useState<string[]>(initData.backHeights || []);
  const numericMeasurements = useMemo(() => measurementsFromForm({
    numberOfSections, numberOfSectionsBack, frontHeights, backHeights,
    stackLengthFront, stackLengthBack, measureBothSides,
  }), [numberOfSections, numberOfSectionsBack, frontHeights, backHeights, stackLengthFront, stackLengthBack, measureBothSides]);
  const geometry = useMemo(() => numericMeasurements ? calculateStackGeometry(numericMeasurements) : null, [numericMeasurements]);
  const averageStackHeight = geometry?.averageHeightM ? beautifyNumber(displayInventoryValue(geometry.averageHeightM, "heightM"), 2) : "";
  const averageStackLength = geometry?.averageLengthM ? beautifyNumber(displayInventoryValue(geometry.averageLengthM, "lengthM"), 2) : "";
  // Stack states
  const [initSpeciesArray, setInitSpeciesArray] = useState<RawWoodSpeciesItem[]>(
    initData.species || [],
  );
  const speciesRef = useRef<RawWoodInputInformationSpeciesHandle>(null);
  const latestSpeciesRef = useRef<RawWoodSpeciesItem[]>(initData.species || []);
  const baselineSpeciesRef = useRef<RawWoodSpeciesItem[]>(initData.species || []);
  const [stackingValue, setStackingValue] = useState<string>(
    initData.stackingValue || stackingOptions[0].id,
  );
  const [straightnessValue, setStraightnessValue] = useState<string>(
    initData.straightnessValue || straightnessOptions[0].id,
  );
  const [processingValue, setProcessingValue] = useState<string>(
    initData.processingValue || processingOptions[0].id,
  );
  const [snowIceValue, setSnowIceValue] = useState<string>(
    initData.snowIceValue || snowIceOptions[0].id,
  );
  const [residuesValue, setResiduesValue] = useState<string>(
    initData.residuesValue || residuesOptions[0].id,
  );
  const [rejectsValue, setRejectsValue] = useState<string>(
    initData.rejectsValue || rejectsOptions[0].id,
  );
  const [battensValue, setBattensValue] = useState<string>(
    initData.battensValue || battensOptions[0].id,
  );
  const [inputModeBattens, setInputModeBattens] = useState<"count" | "volume">(
    initData.inputModeBattens || "volume",
  );
  const [inputModeRejects, setInputModeRejects] = useState<"count" | "volume">(
    initData.inputModeRejects || "volume",
  );

  // Wood Chip states
  const [woodChipManualVolume, setWoodChipManualVolume] = useState<string>(
    initData.woodChipManualVolume || "",
  );
  const [woodChipManualCompFactor, setWoodChipManualCompFactor] = useState<string>(
    initData.woodChipManualCompFactor || "",
  );

  // Wood log states
  const [woodLogAssortment, setWoodLogAssortment] = useState<string>(
    inventoryAssortment || initData.assortment || woodLogAssortmentOptions[0].id,
  );
  const [initLogRows, setInitLogRows] = useState<LogEntry[]>(initData.logRows || []);
  const baselineLogRowsRef = useRef<LogEntry[]>(initData.logRows || []);
  const logRowsRef = useRef<RawWoodInputMeasurementLogsHandler>(null);
  // Handling data
  const [selectedLoggingCompanyString, setSelectedLoggingCompanyString] = useState<string>(() => {
    if (loggingCompany && loggingCompany.name) {
      return loggingCompany.name;
    }
    return "";
  });
  const [selectedForwardingCompanyString, setSelectedForwardingCompanyString] = useState<string>(
    () => {
      if (removalCompany && removalCompany.name) {
        return removalCompany.name;
      }
      return "";
    },
  );
  const [selectedChippingCompanyString, setSelectedChippingCompanyString] = useState<string>(() => {
    if (chippingCompany && chippingCompany.name) {
      return chippingCompany.name;
    }
    return "";
  });
  const [selectedLoggingCompany, setSelectedLoggingCompany] = useState<ContactType[]>(() => {
    if (loggingCompany) {
      // First check for userId match
      if (loggingCompany.userId) {
        const companyData = handlingCompaniesOptions.find(
          (company) => company.id === loggingCompany.userId,
        );
        if (companyData && companyData.id) {
          return [companyData];
        }
      }
      // Else check for orgId match
      if (loggingCompany.id) {
        const companyData = handlingCompaniesOptions.find(
          (company) => company.orgId === loggingCompany.id,
        );
        if (companyData && companyData.id) {
          return [companyData];
        }
      }
    } else if (handlingCompaniesOptions.length === 1) {
      return [handlingCompaniesOptions[0]];
    }
    return [];
  });
  const [selectedForwardingCompany, setSelectedForwardingCompany] = useState<ContactType[]>(() => {
    if (removalCompany) {
      // First check for userId match
      if (removalCompany.userId) {
        const companyData = handlingCompaniesOptions.find(
          (company) => company.id === removalCompany.userId,
        );
        if (companyData && companyData.id) {
          return [companyData];
        }
      }
      // Else check for orgId match
      if (removalCompany.id) {
        const companyData = handlingCompaniesOptions.find(
          (company) => company.orgId === removalCompany.id,
        );
        if (companyData && companyData.id) {
          return [companyData];
        }
      }
    } else if (handlingCompaniesOptions.length === 1) {
      return [handlingCompaniesOptions[0]];
    }
    return [];
  });
  const [selectedChippingCompany, setSelectedChippingCompany] = useState<ContactType[]>(() => {
    if (chippingCompany) {
      if (chippingCompany.userId) {
        const companyData = handlingCompaniesOptions.find(
          (company) => company.id === chippingCompany.userId,
        );
        if (companyData && companyData.id) return [companyData];
      }
      if (chippingCompany.id) {
        const companyData = handlingCompaniesOptions.find(
          (company) => company.orgId === chippingCompany.id,
        );
        if (companyData && companyData.id) return [companyData];
      }
    } else if (handlingCompaniesOptions.length === 1) {
      return [handlingCompaniesOptions[0]];
    }
    return [];
  });
  const [selectedBuyer, setSelectedBuyer] = useState<ContactType[]>(() => {
    if (buyer) {
      // Check if buyer id is equal to current orgId
      // if (buyer.id && buyer.id === userOrg?.id) {
      //   // Set nameAndOrgName for buyer option
      //   let nameAndOrgName = buyer.name;
      //   if (user && user.id === buyer.userId && user.name) {
      //     nameAndOrgName = `${user.name} (${buyer.name})`;
      //   }
      //   return [
      //     {
      //       id: buyer.id,
      //       label: `${buyer.id}:${buyer.name}`,
      //       orgName: buyer.name,
      //       nameAndOrgName: nameAndOrgName,
      //     },
      //   ];
      // }
      // Else find in handling companies (first userId, then orgId)
      if (buyer.userId) {
        const companyData = buyerCompanyOptions.find((company) => company.id === buyer.userId);
        if (companyData && companyData.id) {
          return [companyData];
        }
      }
      // Else check for orgId match
      if (buyer.id) {
        const companyData = buyerCompanyOptions.find((company) => company.orgId === buyer.id);
        if (companyData && companyData.id) {
          return [companyData];
        }
      }
    } else if (buyerCompanyOptions.length === 1) {
      return [buyerCompanyOptions[0]];
    }

    return [];
  });
  const [releasedToBuyer, setReleasedToBuyer] = useState<boolean>(approvalStatus(selectedInventoryItem) === "rejected" ? false : buyer?.releasedToBuyer || false);
  const [selectedBuyerApprovalStatus, setSelectedBuyerApprovalStatus] = useState<BuyerStatusChangeProps["status"]>(() => getInventoryBuyerApprovalStatus(buyer));
  const [buyerNote, setBuyerNote] = useState<string>(buyer?.note || "");
  const initialBuyerApprovalRequirements = buyer?.approvalRequirements;
  const [buyerApprovalRequirements, setBuyerApprovalRequirements] = useState<
    NonNullable<NonNullable<FirestoreInventoryItemProps["buyer"]>["approvalRequirements"]>
  >(() =>
    Array.isArray(initialBuyerApprovalRequirements) ? initialBuyerApprovalRequirements : [],
  );
  const buyerChangeStatusRef = useRef<BuyerStatusChangeProps | null>(
    buyer?.statusChange ? buyer.statusChange : null,
  );
  const handleSetBuyerApprovalStatus = (value: BuyerStatusChangeProps["status"]) => {
    setSelectedBuyerApprovalStatus(value);
    buyerChangeStatusRef.current = {
      status: value,
      user: { id: user?.id || "", name: `${user?.firstName || ""} ${user?.lastName || ""}` },
      date: dayjs().format(),
    };
  };

  const buyerApprovalOptions = useMemo(() => {
    return BUYER_APPROVAL_OPTIONS.map((option) => ({
      id: option,
      status: option === "draft" || option === "pending" || !canDecide
        ? "disabled"
        : "enabled",
      label: translate(`raw-wood-input-handling-approval-${option}`),
    }));
  }, [canDecide, translate("render-change")]);

  const [selectedStatus, setSelectedStatus] = useState<string>(() => {
    if (inventoryStatus) return inventoryStatus;
    return statusOptions[0].id;
  });
  const [price, setPrice] = useState<string>(pricePerUnit ? beautifyNumber(pricePerUnit) : "");
  const [inventoryStatusNote, setInventoryStatusNote] = useState<string>(
    inventoryStatusNoteIn || "",
  );
  const [accessibility, setAccessibility] = useState<string[]>(buyer?.accessibility || []);
  const [accessibilityNote, setAccessibilityNote] = useState<string>(buyer?.accessibilityNote || "");

  const [activityList, setActivityList] = useState<FirestoreActivityProps[]>([]);

  const selectedBuyerOrgId = selectedBuyer[0]?.orgId;
  const selectedBuyerOrgData = selectedBuyerOrgId
    ? buyerOrgsById?.[selectedBuyerOrgId]
    : undefined;
  const buyerApprovalRequirementsLoaded =
    !selectedBuyerOrgId || selectedBuyerOrgData !== undefined;

  useEffect(() => {
    if (!selectedBuyerOrgId) {
      setBuyerApprovalRequirements([]);
      return;
    }

    const configuredRequirements = selectedBuyerOrgData?.buyerApprovalRequirements;
    const configuredRequirementSnapshots = (
      Array.isArray(configuredRequirements) ? configuredRequirements : []
    )
      .filter((requirement) => requirement.status === "active")
      .map((requirement) => ({
        id: requirement.id,
        type: requirement.type,
        label: requirement.label,
        required: requirement.required,
        requirementMet: false,
      }));

    // Existing inventory snapshots retain the seller's confirmations. Add any
    // newly active policy requirements so an updated buyer policy is editable
    // and can be met before the inventory is released.
    const savedBuyerApprovalRequirements = buyer?.approvalRequirements;
    if (
      buyer?.id === selectedBuyerOrgId &&
      Array.isArray(savedBuyerApprovalRequirements) &&
      savedBuyerApprovalRequirements.length
    ) {
      const missingRequirements = configuredRequirementSnapshots.filter(
        (configuredRequirement) =>
          !savedBuyerApprovalRequirements.some(
            (savedRequirement) =>
              savedRequirement.id === configuredRequirement.id &&
              savedRequirement.type === configuredRequirement.type,
          ),
      );
      setBuyerApprovalRequirements([...savedBuyerApprovalRequirements, ...missingRequirements]);
      return;
    }

    if (!selectedBuyerOrgData) return;
    setBuyerApprovalRequirements(configuredRequirementSnapshots);
  }, [buyer?.approvalRequirements, buyer?.id, selectedBuyerOrgData, selectedBuyerOrgId]);

  // --- Effects --- //
  useEffect(() => {
    if (
      persistedLock ||
      !buyerSelectionLocked ||
      !lockedBuyerOption ||
      selectedBuyer[0]?.id === lockedBuyerOption.id
    ) {
      return;
    }

    // Changing the buyer invalidates the prior buyer's release and approval state.
    setReleasedToBuyer(false);
    handleSetBuyerApprovalStatus("draft");
    setBuyerNote("");
    setSelectedBuyer([lockedBuyerOption]);
  }, [persistedLock, buyerSelectionLocked, lockedBuyerOption, selectedBuyer]);

  const { speciesAndPercentage } = useThinningData(activityList);

  const originsOptions = useMemo(() => {
    const options = getActivityOptions({
      projects: projectOptions,
      activities: activities,
      activityGroups: ["logging", "chipping"],
      linkedActivity: originsIn,
      estateId: originsIn?.estateId || "",
      mapId: originsIn?.mapId || "",
      mapName: originsIn?.mapName || "",
    });
    // Find selected origin from input data
    if (originsIn && origins.length === 0) {
      const selectedOrigin = options.find((option) =>
        option.projectId === originsIn.projectId && option.activityId === originsIn.activityId);
      if (selectedOrigin) {
        setOrigins([selectedOrigin]);
        const { projectId, activityId } = selectedOrigin;
        // Find species from activity and set species if not already set
        if (
          projectId &&
          activityId &&
          activities[projectId][activityId] &&
          initSpeciesArray.length === 0 &&
          !speciesChanged(speciesRef.current?.getSpeciesArray() ?? latestSpeciesRef.current, baselineSpeciesRef.current)
        ) {
          const activityData = activities[projectId][activityId];
          if (
            activityData.biomassScreening?.species &&
            activityData.biomassScreening.species.length > 0
          ) {
            const speciesArray: RawWoodSpeciesItem[] = [];
            const numberOfSpecies = activityData.biomassScreening.species.length;
            activityData.biomassScreening.species.forEach((spc, index) => {
              speciesArray.push({
                uid: index + 1,
                id: spc,
                mix: (Math.round(100 / numberOfSpecies / 10) * 10).toFixed(0),
              });
            });
            baselineSpeciesRef.current = speciesArray;
            latestSpeciesRef.current = speciesArray;
            setInitSpeciesArray(speciesArray);
          }
        }
        const screeningCertificationNumbers = selectedOrigin.biomassScreeningData;
        const certificationNumbersToCopy = [
          ["fsc", screeningCertificationNumbers?.fscCertificationNumber],
          ["pefc", screeningCertificationNumbers?.pefcCertificationNumber],
        ] as const;
        const tagsToAdd = certificationNumbersToCopy
          .filter(([, number]) => Boolean(number))
          .map(([tag]) => tag);
        if (tagsToAdd.length > 0) {
          setCertification((previous) => Array.from(new Set([...previous, ...tagsToAdd])));
          setCertificationNumbers((previous) => {
            const next = { ...previous };
            certificationNumbersToCopy.forEach(([tag, number]) => {
              if (number && !next[tag]) next[tag] = number;
            });
            return next;
          });
        }
      }
    }
    return options;
  }, [originsIn, projectOptions, activities]);

  // Fetch inventory files when inventory changes
  useEffect(() => {
    if (selectedInventoryItem.id && !inventoryFiles[selectedInventoryItem.id]) {
      fetchFiles({ rootFolder: "inventories", rootId: selectedInventoryItem.id });
    }
  }, [selectedInventoryItem.id]);

  // Init species array from speciesAndPercentage from thinning data
  useEffect(() => {
    if (initSpeciesArray.length === 0 && speciesAndPercentage.length > 0) {
      // Get array of species names and their mix percentages
      const speciesArray: RawWoodSpeciesItem[] = [];
      const names: string[] = [];
      speciesAndPercentage.forEach((item, index) => {
        if (item.id && !names.includes(item.id)) {
          names.push(item.id);
          speciesArray.push({
            uid: index + 1,
            id: item.id,
            mix: "",
          });
        }
      });
      // Loaded defaults form the initial state, not an unsaved user edit.
      if (!speciesChanged(speciesRef.current?.getSpeciesArray() ?? latestSpeciesRef.current, baselineSpeciesRef.current)) {
        baselineSpeciesRef.current = speciesArray;
        latestSpeciesRef.current = speciesArray;
        setInitSpeciesArray(speciesArray);
      }
    }
  }, [speciesAndPercentage]);

  useEffect(() => {
    latestSpeciesRef.current = initSpeciesArray;
  }, [initSpeciesArray]);

  // useEffect(() => {
  //   const initial = initData.species || [];
  //   baselineSpeciesRef.current = initial;
  //   latestSpeciesRef.current = initial;
  //   setInitSpeciesArray(initial); // only for rendering the species UI
  //   // also consider resetting tabValue etc if you do that elsewhere
  // }, [selectedInventoryItem.id]);

  // --- Functions --- //
  function makeDirtySetter<T>(setter: (v: T) => void) {
    return (v: T) => {
      setDataChanged(true);
      setter(v);
    };
  }
  const snapshotSpeciesFromChild = () => {
    const arr = speciesRef.current?.getSpeciesArray();
    if (arr) {
      latestSpeciesRef.current = arr;
      return arr;
    }
    return latestSpeciesRef.current;
  };

  const handleSave = async () => {
    // Set loading state
    setLoading(true);
    if (persistedLock) {
      try {
        if (!canDecide) throw new Error("inventory-stale");
        const action = selectedBuyerApprovalStatus === "approved" ? "approve"
          : selectedBuyerApprovalStatus === "rejected" ? "reject" : undefined;
        await saveInventoryEditor({ expected: baselineInventory, actor, action, note: buyerNote });
        handleClose();
      } catch { reportSaveError(); }
      finally { setLoading(false); }
      return;
    }
    if (!canEditStack) { setLoading(false); return; }
    let preparedInventory: SaveInventoryItemProps = {};
    const preparedMeasurements: SaveInventoryMeasurementProps[] = [];
    // Get selected species array
    snapshotSpeciesFromChild();
    const speciesArray = latestSpeciesRef.current;
    try {
      // Prepare the data to be saved (some is inventory specific and some are measurement specific).
      // Also, it can be stack, wood chip stack og logs
      const inventorySaveData: SaveInventoryItemProps = { ...selectedInventoryItem };
      if (variant === "WoodChipStack") {
        inventorySaveData.isChipped = isChipped;
      }
      // Set origins
      if (origins.length > 0) {
        const { projectId, activityId, projectName, activityName, activityTypeId } = origins[0];
        inventorySaveData.origins = {
          ...selectedInventoryItem.origins,
          type: "activity",
          projectId,
          activityId,
          projectName,
          activityName,
        };
        if (activityTypeId) {
          // Get activity data from id
          const activityData = getActivityData(activityTypeId);
          if (activityData) {
            inventorySaveData.origins.activityType = { ...activityData };
          }
        }
      } else {
        const { estateId, estateName, mapId, mapName } = selectedInventoryItem.origins;
        inventorySaveData.origins = {
          estateId,
          estateName,
          mapId,
          mapName,
          type: "manual",
        } as SaveInventoryItemProps["origins"];
      }
      // Set certification
      inventorySaveData.certification = certification as SaveInventoryItemProps["certification"];
      // Set certification number
      inventorySaveData.certificationNumber =
        certificationNumber as SaveInventoryItemProps["certificationNumber"];
      inventorySaveData.certificationNumbers = Object.fromEntries(
        Object.entries(certificationNumbers).filter(
          ([tag, number]) => certification.includes(tag) && Boolean(number),
        ),
      ) as SaveInventoryItemProps["certificationNumbers"];
      inventorySaveData.certificationClaims = Object.fromEntries(
        Object.entries(certificationClaims).filter(
          ([tag, claim]) => certification.includes(tag) && Boolean(claim),
        ),
      ) as SaveInventoryItemProps["certificationClaims"];

      // Set responsible
      if (responsible.length > 0) {
        const { id, email, name, personType } = responsible[0];
        inventorySaveData.measurementResponsible = {
          id: id ? id : "",
          email: email ? email : "",
          name: name ? name : "",
          type: personType ? (personType as "team" | "contact") : "team",
        };
      }
      // Set logging company
      if (selectedLoggingCompany.length > 0) {
        const companyData = handlingCompaniesOptions.find(
          (company) => company.id === selectedLoggingCompany[0].id,
        );
        if (companyData) {
          inventorySaveData.loggingCompany = {
            id: companyData.orgId ? companyData.orgId : "",
            userId: companyData.id ? companyData.id : "",
            name: companyData.orgName ? companyData.orgName : "",
            email: companyData.email ? companyData.email : "",
          };
        }
      } else if (selectedLoggingCompanyString && selectedLoggingCompanyString !== "") {
        inventorySaveData.loggingCompany = {
          name: selectedLoggingCompanyString,
        };
      } else {
        inventorySaveData.loggingCompany = null;
      }
      // Set removal company
      if (selectedForwardingCompany.length > 0) {
        const companyData = handlingCompaniesOptions.find(
          (company) => company.id === selectedForwardingCompany[0].id,
        );
        if (companyData) {
          inventorySaveData.removalCompany = {
            id: companyData.orgId ? companyData.orgId : "",
            userId: companyData.id ? companyData.id : "",
            name: companyData.orgName ? companyData.orgName : "",
            email: companyData.email ? companyData.email : "",
          };
        }
      } else if (selectedForwardingCompanyString && selectedForwardingCompanyString !== "") {
        inventorySaveData.removalCompany = {
          name: selectedForwardingCompanyString,
        };
      } else {
        inventorySaveData.removalCompany = null;
      }
      // Set chipping company for wood chip stacks
      if (variant === "WoodChipStack") {
        if (selectedChippingCompany.length > 0) {
          const companyData = handlingCompaniesOptions.find(
            (company) => company.id === selectedChippingCompany[0].id,
          );
          if (companyData) {
            inventorySaveData.chippingCompany = {
              id: companyData.orgId ? companyData.orgId : "",
              userId: companyData.id ? companyData.id : "",
              name: companyData.orgName ? companyData.orgName : "",
              email: companyData.email ? companyData.email : "",
            };
          }
        } else if (selectedChippingCompanyString && selectedChippingCompanyString !== "") {
          inventorySaveData.chippingCompany = {
            name: selectedChippingCompanyString,
          };
        } else {
          inventorySaveData.chippingCompany = null;
        }
      }

      // Set buyer
      if (selectedBuyer.length > 0) {
        const buyerApprovalRequirementsForSave = buyerApprovalRequirements.map((requirement) =>
          requirement.type === "accessibility"
            ? { ...requirement, requirementMet: accessibility.length > 0 }
            : requirement,
        );
        const buyerAccessibilityData = buyerApprovalRequirementsForSave.some(
          (requirement) => requirement.type === "accessibility",
        )
          ? { accessibility, accessibilityNote }
          : {};
        const companyData = buyerCompanyOptions.find(
          (company) => company.id === selectedBuyer[0].id,
        );
        if (companyData && companyData.id !== userOrg?.id) {
          // if (companyData) {
          inventorySaveData.buyer = {
            id: companyData?.orgId ? companyData.orgId : "",
            userId: companyData?.id ? companyData.id : "",
            name: companyData?.orgName ? companyData.orgName : "",
            email: companyData?.email ? companyData.email : "", // Note: this is the contacts email
            note: buyerNote,
            releasedToBuyer: releasedToBuyer,
            approvalRequirements: buyerApprovalRequirementsForSave,
            ...buyerAccessibilityData,
            statusChange: buyerChangeStatusRef.current,
          };
        } else if (inventorySaveData.buyer) {
          inventorySaveData.buyer = {
            ...inventorySaveData.buyer,
            note: buyerNote,
            approvalRequirements: buyerApprovalRequirementsForSave,
            ...buyerAccessibilityData,
            statusChange: buyerChangeStatusRef.current,
          };
        }
      } else {
        inventorySaveData.buyer = null;
      }
      // Set seller user
      // Only set seller user if userOrg.id is the same as inventory orgId
      if (inventorySaveData.orgId === userOrg?.id) {
        inventorySaveData.sellerUser = {
          id: user?.id || "",
          email: user?.email || "",
          name: `${user?.firstName || ""} ${user?.lastName || ""}`.trim(),
        };
      }
      // Persist a status even when the legacy record had no statusChange.
      if (inventorySaveData.buyer) {
        inventorySaveData.buyer.statusChange = buyerChangeStatusRef.current ?? {
          status: selectedBuyerApprovalStatus,
          user: { id: user?.id || "", name: `${user?.firstName || ""} ${user?.lastName || ""}`.trim() },
          date: dayjs().format(),
        };
        inventorySaveData.buyer.releasedToBuyer = releasedToBuyer;
      }
      // Set felling date
      inventorySaveData.fellingDate = date ? dayjs(date).format() : dayjs().format();
      if (AppConfig.inventoryData.inventoryStatus !== "hidden") {
        inventorySaveData.inventoryStatus =
          selectedStatus as SaveInventoryItemProps["inventoryStatus"];
        inventorySaveData.inventoryStatusNote = inventoryStatusNote;
      }
      // Set price
      inventorySaveData.pricePerUnit = price ? parseFloat(price.replace(",", ".")) : 0;
      // Save currency based on country code
      const countryCode = (userOrg?.countryCode || "DK") as TCountryCode;
      const countryData = getCountryData(countryCode);
      inventorySaveData.currency = countryData.currency[0] || "DKK";
      // Add certification tags to categories if not already there.
      // Also remove any old certification tags that are not in the current certification state
      const updatedCategories = inventorySaveData.categories || [];
      if (certification && certification.length > 0) {
        certification.forEach((cert) => {
          if (!updatedCategories.includes(cert)) {
            updatedCategories.push(cert);
          }
        });
        // Remove any old certification tags that are not in the current certification state
        updatedCategories.forEach((cat) => {
          //@ts-expect-error
          if (certificationTagsOptions.includes(cat) && !certification.includes(cat)) {
            const index = updatedCategories.indexOf(cat);
            if (index > -1) {
              updatedCategories.splice(index, 1);
            }
          }
        });
      }
      // Also add assortment to inventory item (needed for logstacks as they may not have any measurement data)
      inventorySaveData.assortment = null;
      if (variant === "WoodLogStack" && woodLogAssortment) {
        inventorySaveData.assortment = woodLogAssortment;
      } else if (assortment) {
        inventorySaveData.assortment = assortment;
      }
      inventorySaveData.categories = updatedCategories;
      // Save inventory item
      preparedInventory = inventorySaveData;
    } catch (error) {
      console.error("Error saving inventory item:", error);
      reportSaveError();
      setLoading(false);
      return;
    }
    // --- Measurement data --- //
    try {
      // Check for variant and prepare measurement data
      if (variant === "WoodStack") {
        let measurementSaveData: SaveInventoryMeasurementProps = {};
        if (measurementData && measurementData.length > 0) {
          const existingMeasurement = measurementData[0];
          measurementSaveData = {
            ...existingMeasurement,
          };
        }
        // Set inventory id
        measurementSaveData.inventoryId = selectedInventoryItem.id;
        // Set unique id for measurement
        if (!measurementSaveData.uniqueMeasurementId) {
          measurementSaveData.uniqueMeasurementId = selectedInventoryItem.uniqueInventoryId;
        }
        // Set species
        measurementSaveData.species = null;
        if (speciesArray.length > 0 && speciesArray[0].id !== "non") {
          measurementSaveData.species = speciesArray
            .filter((spc) => spc.id !== "non")
            .map((spc) => ({
              id: spc.id,
              mix: isNaN(parseInt(spc.mix)) ? 100 : parseInt(spc.mix),
            }));
        }
        // Set assortment
        measurementSaveData.assortment = null;
        if (assortment) {
          measurementSaveData.assortment =
            assortment as SaveInventoryMeasurementProps["assortment"];
        }
        // Set loglength
        measurementSaveData.logLengthM = null;
        if (averageLogLength) {
          measurementSaveData.logLengthM = parseFloat(averageLogLength.replace(",", "."));
        }
        // Set average log diameter
        measurementSaveData.averageDiameterM = null;
        if (averageLogDiameter) {
          measurementSaveData.averageDiameterM =
            parseFloat(averageLogDiameter.replace(",", ".")) / 100;
        }
        // Set stack length front
        measurementSaveData.stackLengthFrontM = null;
        if (stackLengthFront) {
          measurementSaveData.stackLengthFrontM = parseFloat(stackLengthFront.replace(",", "."));
        }
        // Set stack length back
        measurementSaveData.stackLengthBackM = null;
        if (stackLengthBack) {
          measurementSaveData.stackLengthBackM = parseFloat(stackLengthBack.replace(",", "."));
        }
        // Counts are independent. Blank draft fields are not stored as NaN or zero.
        measurementSaveData.frontHeightsM = heightsForSave(frontHeights, numberOfSections);
        measurementSaveData.backHeightsM = measureBothSides ? heightsForSave(backHeights, numberOfSectionsBack) : null;
        if (!measureBothSides) measurementSaveData.stackLengthBackM = null;
        // Set stacking parameters
        let stackingParameters: SaveInventoryMeasurementProps["stackingParameters"] = {
          ...(measurementSaveData.stackingParameters ?? {}),
        };
        if (stackingValue) {
          // @ts-ignore
          stackingParameters.stackingMethod = stackingValue;
        }
        if (straightnessValue) {
          // @ts-ignore
          stackingParameters.logStraightness = straightnessValue;
        }
        if (processingValue) {
          // @ts-ignore
          stackingParameters.processingResult = processingValue;
        }
        if (snowIceValue) {
          // @ts-ignore
          stackingParameters.snowAndIceCoverage = snowIceValue;
        }
        if (residuesValue) {
          // @ts-ignore
          stackingParameters.loggingResidue = residuesValue;
        }
        measurementSaveData.stackingParameters = stackingParameters;
        // Set number of cull and support logs
        measurementSaveData.numberOfSupportLogs = 0;
        if (battensValue !== "" && battensValue !== "0" && inputModeBattens === "count") {
          measurementSaveData.numberOfSupportLogs = parseInt(battensValue);
        }
        measurementSaveData.numberOfCullLogs = 0;
        if (rejectsValue !== "" && rejectsValue !== "0" && inputModeRejects === "count") {
          measurementSaveData.numberOfCullLogs = parseInt(rejectsValue);
        }
        measurementSaveData.volumeOfSupportLogsM3 = 0;
        if (battensValue !== "" && battensValue !== "0" && inputModeBattens === "volume") {
          measurementSaveData.volumeOfSupportLogsM3 = parseFloat(battensValue.replace(",", "."));
        }
        measurementSaveData.volumeOfCullLogsM3 = 0;
        if (rejectsValue !== "" && rejectsValue !== "0" && inputModeRejects === "volume") {
          measurementSaveData.volumeOfCullLogsM3 = parseFloat(rejectsValue.replace(",", "."));
        }
        // Save measurement data
        preparedMeasurements.push(measurementSaveData);
      } else if (variant === "WoodChipStack") {
        let measurementSaveData: SaveInventoryMeasurementProps = {};
        if (measurementData && measurementData.length > 0) {
          const existingMeasurement = measurementData[0];
          measurementSaveData = {
            ...existingMeasurement,
          };
        }
        // Set inventory id
        measurementSaveData.inventoryId = selectedInventoryItem.id;
        // Set unique id for measurement
        if (!measurementSaveData.uniqueMeasurementId) {
          measurementSaveData.uniqueMeasurementId = selectedInventoryItem.uniqueInventoryId;
        }
        // Set species
        // measurementSaveData.species = null;
        // if (woodChipSpecies) {
        //   measurementSaveData.species = [{ id: woodChipSpecies, mix: 100 }];
        // }
        measurementSaveData.species = null;
        if (speciesArray.length > 0 && speciesArray[0].id !== "non") {
          measurementSaveData.species = speciesArray
            .filter((spc) => spc.id !== "non")
            .map((spc) => ({
              id: spc.id,
              mix: isNaN(parseInt(spc.mix)) ? 100 : parseInt(spc.mix),
            }));
        }
        // Set loglength
        measurementSaveData.logLengthM = null;
        if (!isChipped && averageLogLength) {
          measurementSaveData.logLengthM = parseFloat(averageLogLength.replace(",", "."));
        }
        // Set stack length front
        measurementSaveData.stackLengthFrontM = null;
        if (stackLengthFront) {
          measurementSaveData.stackLengthFrontM = parseFloat(stackLengthFront.replace(",", "."));
        }
        measurementSaveData.frontHeightsM = heightsForSave(frontHeights, numberOfSections);
        measurementSaveData.backHeightsM = null;
        measurementSaveData.stackLengthBackM = null;
        // Set wood chip manual volume
        measurementSaveData.manualVolumeM3 = null;
        if (woodChipManualVolume) {
          measurementSaveData.manualVolumeM3 = parseLocalizedNumber(woodChipManualVolume);
        }
        // Set wood chip manual comp factor
        measurementSaveData.manualCorrectionPercentage = null;
        if (woodChipManualCompFactor) {
          measurementSaveData.manualCorrectionPercentage = parseMeasurementNumber(woodChipManualCompFactor);
        }
        // Save measurement data
        preparedMeasurements.push(measurementSaveData);
      } else if (variant === "WoodLogStack") {
        // Get log rows from logRowsRef
        const logRows = (await logRowsRef.current?.getLogRows()) || initLogRows || [];
        // Prepare measurement data for WoodLogStack. Remember it is an array of data, so there are several measurements
        const measurementSaveData: SaveInventoryMeasurementProps[] = [];
        logRows.forEach((logRow) => {
          let measurementData: SaveInventoryMeasurementProps = {};
          // Unpack log row data
          const { id, uniqueMeasurementId, species, length, diameter, diameter2, qClass } = logRow;
          // If no unique measurement id, skip this log row
          if (!uniqueMeasurementId) {
            console.warn("Log row has no unique measurement id, skipping:", logRow);
            return;
          }
          // Insert unique id for measurement
          measurementData.uniqueMeasurementId = uniqueMeasurementId;
          // Insert unique inventory id
          measurementData.inventoryId = selectedInventoryItem.id;
          // Set species
          measurementData.species = null;
          if (species && species !== "") {
            measurementData.species = [{ id: species, mix: 100 }];
          }
          // Set assortment
          measurementData.assortment = null;
          if (woodLogAssortment) {
            measurementData.assortment =
              woodLogAssortment as SaveInventoryMeasurementProps["assortment"];
          }
          // Set log length
          measurementData.logLengthM = null;
          if (length && length !== "") {
            measurementData.logLengthM = parseFloat(length.replace(",", "."));
          }
          // Set average diameter
          measurementData.averageDiameterM = null;
          if (diameter && diameter !== "") {
            measurementData.averageDiameterM = parseFloat(diameter.replace(",", ".")) / 100;
          }
          // Set second diameter if available
          measurementData.averageDiameter2M = null;
          if (diameter2 && diameter2 !== "") {
            measurementData.averageDiameter2M = parseFloat(diameter2.replace(",", ".")) / 100;
          }
          // Set class
          measurementData.classification = null;
          if (qClass && qClass !== "") {
            measurementData.classification = qClass;
          }
          // Set id if not begins with "new-"
          if (id && !id.startsWith("new-")) {
            measurementData.id = id;
          }
          measurementSaveData.push(measurementData);
        });
        // Iterate over measuremetnSaveData and save each measurement
        for (const measurement of measurementSaveData) {
          preparedMeasurements.push(measurement);
        }
      }
      await saveInventoryEditor({
        expected: baselineInventory,
        data: preparedInventory,
        measurements: preparedMeasurements,
        expectedMeasurements: baselineMeasurements.current,
        actor,
        action: releasedToBuyer ? "submit" : undefined,
      });
    } catch (error) {
      console.error("Error saving inventory data:", error);
      reportSaveError();
      setLoading(false);
      return;
    }
    // Close the input dialog
    setLoading(false);
    handleClose();
  };

  async function saveFiles(categories: string[], files: File[]) {
    // Get inventory id
    const inventoryId = selectedInventoryItem.id;
    const { origins } = selectedInventoryItem;
    if (!inventoryId) {
      console.error("No inventory id found, cannot save files.");
      return;
    }
    // Check for existing file with category "deliveryNote" and replace it
    const currentFiles = inventoryFiles[selectedInventoryItem.id]
      ? Object.values(inventoryFiles[selectedInventoryItem.id])
      : [];
    let fileId = undefined;
    if (currentFiles && currentFiles.length > 0) {
      currentFiles.forEach((file) => {
        if (file.categories && file.categories.includes("deliveryNote")) {
          fileId = file.id;
          // Exit loop
          return;
        }
      });
    }
    // Create fileData
    const fileData: UploadFileEntryProps = {
      id: fileId,
      bucket: config.DEFAULT_FILES_BUCKET,
      categories: categories,
      uploadedBy: {
        id: user?.id,
        name: user?.firstName + " " + user?.lastName,
        email: user?.email,
        orgId: user?.orgId,
      },
      orgId: userOrg?.id || null,
      estateId: origins.estateId || null,
      mapId: origins.mapId || null,
      projectId: origins.projectId || null,
      activityId: origins.activityId || null,
      inventoryId: inventoryId,
      featureIds: null,
      featureCategories: null,
    };
    // Upload files
    await uploadFiles({
      fileDoc: fileData,
      files: files,
      bucket: config.DEFAULT_FILES_BUCKET,
      rootFolder: "inventories",
      rootId: inventoryId,
    });
  }

  const handleChangeTabValue = async (event: React.SyntheticEvent, newValue: number) => {
    // Get species array from speciesRef
    const speciesArray = snapshotSpeciesFromChild();
    // Set initial species array to the returned species array
    setInitSpeciesArray(speciesArray);
    if (logRowsChanged(logRowsRef.current?.getCurrentLogRows() ?? initLogRows, baselineLogRowsRef.current)) {
      setDataChanged(true);
    }
    // Set initial log rows to the logRowsRef
    logRowsRef.current?.getLogRows().then((rows) => {
      if (rows && rows.length > 0) {
        setInitLogRows(rows);
      }
    });
    // Update the tab value
    setTabValue(newValue);
  };

  const handleCloseInput = () => {
    snapshotSpeciesFromChild();
    const speciesChangedFlag = speciesChanged(latestSpeciesRef.current, baselineSpeciesRef.current);
    const logsChangedFlag = logRowsChanged(
      logRowsRef.current?.getCurrentLogRows() ?? initLogRows,
      baselineLogRowsRef.current,
    );
    if (dataChanged || speciesChangedFlag || logsChangedFlag) {
      setOpenConfirmDialog(true);
    } else {
      setDataChanged(false);
      handleClose();
    }
  };

  const handleCancelConfirmDialog = () => {
    setOpenConfirmDialog(false);
  };

  const handleConfirmDialog = async () => {
    setLoading(true);
    // Fetch log rows to remove reserved unique IDs from new entries not used
    const logRows = (await logRowsRef.current?.getLogRows()) || initLogRows;
    try {
      if (logRows && logRows.length > 0) {
        // Check for log rows where id starts with "new-"
        const newLogRows = logRows.filter((row) => row.id.startsWith("new-"));
        await Promise.all(
          newLogRows.map((row) =>
            callCloudFunction("releaseReservedId", {
              orgId: userOrg?.id,
              id: row.uniqueMeasurementId,
              group: "WoodLog",
            }),
          ),
        );
      }
    } catch (error) {
      console.error("Error releasing unique IDs:", error);
    }
    setOpenConfirmDialog(false);
    setLoading(false);
    baselineSpeciesRef.current = latestSpeciesRef.current;
    setDataChanged(false);
    // resetState();
    handleClose();
  };

  const handleSetCertification = (certificationTag: string) => {
    if (certification.includes(certificationTag)) {
      setCertification((previous) => previous.filter((item) => item !== certificationTag));
      if (certificationTag === "fsc" || certificationTag === "pefc") {
        setCertificationClaims((previous) => {
          const next = { ...previous };
          delete next[certificationTag];
          return next;
        });
      }
      return;
    }

    setCertification((previous) => [...previous, certificationTag]);
  };

  const handleSetOrigins = (v: any) => {
    if (v.length === 0) {
      setOrigins([]);
      setInitSpeciesArray([]);
      return;
    }
    const lastItem = v.slice(-1)[0];
    setOrigins([lastItem]);
    // Find associated activity
    const { projectId, activityId, biomassScreeningData } = lastItem;
    const activity = activities[projectId]?.[activityId];
    const certificationNumbersToCopy = [
      ["fsc", biomassScreeningData?.fscCertificationNumber],
      ["pefc", biomassScreeningData?.pefcCertificationNumber],
    ] as const;
    if (activity) {
      setActivityList([activity]);
      // Also, preload date if available
      const { executedDate, expireDate, lastModifiedDate } = activity;

      if (executedDate) {
        setDate(dayjs(executedDate).toDate());
      } else if (expireDate) {
        setDate(dayjs(expireDate).toDate());
      } else if (lastModifiedDate) {
        setDate(dayjs(lastModifiedDate).toDate());
      } else {
        setDate(dayjs().toDate());
      }
    }
    // Set species from activity if not already set
    if (
      activity &&
      activity.biomassScreening &&
      activity.biomassScreening.species &&
      activity.biomassScreening.species.length > 0
    ) {
      const newSpeciesArray: RawWoodSpeciesItem[] = [];
      const numberOfSpecies = activity.biomassScreening.species.length;
      activity.biomassScreening.species.forEach((spc, index) => {
        newSpeciesArray.push({
          uid: index + 1,
          id: spc,
          mix: (Math.round(100 / numberOfSpecies / 10) * 10).toFixed(0),
        });
      });
      setInitSpeciesArray(newSpeciesArray);
      baselineSpeciesRef.current = newSpeciesArray;
      latestSpeciesRef.current = newSpeciesArray;
    }
    const tagsToAdd = certificationNumbersToCopy
      .filter(([, number]) => Boolean(number))
      .map(([tag]) => tag);
    if (tagsToAdd.length > 0) {
      setCertification((previous) => Array.from(new Set([...previous, ...tagsToAdd])));
      setCertificationNumbers((previous) => {
        const next = { ...previous };
        certificationNumbersToCopy.forEach(([tag, number]) => {
          if (number && !next[tag]) next[tag] = number;
        });
        return next;
      });
    }
  };

  const handleSetResponsible = (v: any) => {
    if (v.length === 0) {
      setResponsible([]);
      return;
    }
    const lastItem = v.slice(-1)[0];
    setResponsible([lastItem]);
  };

  const handleSetAverageLogLength = (event: React.ChangeEvent<HTMLInputElement>) => {
    const val = strInputNumberCorrection(event.target.value, i18n, 2);
    setAverageLogLength(val);
  };

  const handleSetAverageLogDiameter = (event: React.ChangeEvent<HTMLInputElement>) => {
    let val = strInputNumberCorrection(event.target.value, i18n, 2);
    setAverageLogDiameter(val);
  };

  const handleSetWoodChipVolumeManuel = (event: React.ChangeEvent<HTMLInputElement>) => {
    const val = strInputNumberCorrection(event.target.value, i18n, 2);
    setWoodChipManualVolume(val);
  };

  const handleSetBattensAndRejectsValue = (
    value: string,
    mode: "battens" | "rejects",
    inputMode: "count" | "volume" | "both",
  ) => {
    setDataChanged(true);
    if (inputMode === "count") {
      if (mode === "battens") {
        setBattensValue(value);
      } else if (mode === "rejects") {
        setRejectsValue(value);
      }
    } else if (inputMode === "volume") {
      const val = strInputNumberCorrection(value, i18n, 2);
      if (mode === "battens") {
        setBattensValue(val);
      } else if (mode === "rejects") {
        setRejectsValue(val);
      }
    }
  };

  const applyBuyerRelease = (value: boolean) => {
    setReleasedToBuyer(value);
    const status = getInventoryBuyerReleaseStatus(selectedBuyerApprovalStatus, value);
    if (status !== selectedBuyerApprovalStatus || (value && status === "pending")) {
      handleSetBuyerApprovalStatus(status);
    }
    setDataChanged(true);
    return true;
  };

  const handleReleasedToBuyerChange = async (value: boolean) => {
    if (!canEditStack) return false;
    if (!value) {
      return applyBuyerRelease(value);
    }
    const origin = origins[0];
    if (!origin?.projectId || !origin.activityId) {
      enqueueSnackbar(
        translate("snackbar-raw-wood-input-handling-activity-origin-required"),
        {
          variant: "info",
          autoHideDuration: DEFAULT_SNACKBAR_DISMISS_TIMEOUT,
          action: (key) => <SnackbarDismissAction id={key} text={translate("close")} />,
        },
      );
      return false;
    }
    // Check that raw wood inventory has been filled correctly
    const assortmentToUse = variant === "WoodLogStack" ? woodLogAssortment : assortment;
    // Validate Inventory data
    let fileDocs: FirestoreFileProps[] = [];
    if (inventoryFiles && selectedInventoryItem.id && inventoryFiles[selectedInventoryItem.id]) {
      fileDocs = Object.values(inventoryFiles[selectedInventoryItem.id]).filter(
        (el) => el.inventoryId === selectedInventoryItem.id,
      );
    }

    const result = validateAndInformInventory({
      inventoryTitle: title,
      variant: variant,
      data: {
        date,
        responsible,
        species: speciesRef.current?.getSpeciesArray() || initSpeciesArray || [],
        assortment: assortmentToUse,
        averageLogLength,
        isChipped,
        averageLogDiameter,
        averageStackHeight,
        averageStackLength,
        manualVolumeM3: woodChipManualVolume,
        conversionFactor: woodChipManualCompFactor,
        selectedLoggingCompany,
        selectedLoggingCompanyString,
        selectedForwardingCompany,
        selectedForwardingCompanyString,
        selectedChippingCompany,
        selectedChippingCompanyString,
        certification,
        certificationNumbers,
        certificationClaims,
        countryCode,
        logs: (await logRowsRef.current?.getLogRows()) || initLogRows || [],
      },
      fileDocs,
      ignoredFileCategories: ["deliveryNote"],
      inventoryConfig: AppConfig.inventoryData,
      translate: translate,
    });
    if (!result.valid) {
      enqueueSnackbar(result.errorMessage, {
        variant: "error",
        autoHideDuration: DEFAULT_SNACKBAR_DISMISS_TIMEOUT,
        action: (key) => <SnackbarDismissAction id={key} text={translate("close")} />,
      });
      setReleasedToBuyer(false);
      return false;
    }
    const { biomassScreeningData } = origin;
    if (!biomassScreeningData || !biomassScreeningData.category) {
      enqueueSnackbar(
        `${translate("snackbar-raw-wood-input-handling-no-screening-1")} 
          ${title}. 
          ${translate("snackbar-raw-wood-input-handling-no-screening-2")}`,
        {
          variant: "info",
          autoHideDuration: DEFAULT_SNACKBAR_DISMISS_TIMEOUT,
          action: (key) => <SnackbarDismissAction id={key} text={translate("close")} />,
        },
      );
      return applyBuyerRelease(value);
    }

    // TODO: Uncomment and implement when validation on screening has been agreed
    // Validate screening data
    // let fileDocs: FirestoreFileProps[] = [];
    // if (projectFiles && projectFiles[projectId]) {
    //   fileDocs = Object.values(projectFiles[projectId]).filter(
    //     (el) => el.activityId === activityId,
    //   );
      // const result = validateAndInformBiomassScreening({
      //   inventoryTitle: title,
      //   activityTitle: activityName,
      //   data: biomassScreeningData,
      //   activityOwnerInformation: ownerInformation,
      //   withActivityOwnerInformation: AppConfig.activityData.owner === "enabled" ? true : false,
      //   fileDocs: fileDocs,
      //   translate: translate,
      // });
      // // If result is not complete, show snackbar and do not allow releasing
      // if (!result.valid) {
      //   enqueueSnackbar(result.errorMessage, {
      //     variant: "error",
      //     autoHideDuration: DEFAULT_SNACKBAR_DISMISS_TIMEOUT,
      //     action: (key) => <SnackbarDismissAction id={key} text={translate("close")} />,
      //   });
      //   setReleasedToBuyer(false);
      //   return;
      // }
    applyBuyerRelease(value);
    enqueueSnackbar(`${title} ` + translate("snackbar-raw-wood-input-handling-sent-to-buyer"), {
      variant: "success",
    });
    return true;
  };

  const tabs = () => {
    if (variant === "WoodStack") {
      return (
        <Tabs value={tabValue} onChange={handleChangeTabValue} variant="scrollable">
          <Tab
            label={translate("input-dialog-raw-wood-tab-information")}
            {...A11yProps(0)}
            sx={style}
          />
          <Tab
            label={translate("input-dialog-raw-wood-tab-measurement")}
            {...A11yProps(1)}
            sx={style}
          />
          <Tab
            label={translate("input-dialog-raw-wood-tab-conversion-factor")}
            {...A11yProps(2)}
            sx={style}
          />
          <Tab
            label={translate("input-dialog-raw-wood-tab-handling")}
            {...A11yProps(4)}
            sx={style}
          />
        </Tabs>
      );
    }
    if (variant === "WoodChipStack") {
      return (
        <Tabs value={tabValue} onChange={handleChangeTabValue} variant="scrollable">
          <Tab
            label={translate("input-dialog-raw-wood-tab-information")}
            {...A11yProps(0)}
            sx={style}
          />
          <Tab
            label={translate("input-dialog-raw-wood-tab-quantities")}
            {...A11yProps(1)}
            sx={style}
          />
          <Tab
            label={translate("input-dialog-raw-wood-tab-handling")}
            {...A11yProps(2)}
            sx={style}
          />
        </Tabs>
      );
    }
    if (variant === "WoodLogStack") {
      return (
        <Tabs value={tabValue} onChange={handleChangeTabValue} variant="scrollable">
          <Tab
            label={translate("input-dialog-raw-wood-tab-information")}
            {...A11yProps(0)}
            sx={style}
          />
          <Tab
            label={translate("input-dialog-raw-wood-tab-measurement")}
            {...A11yProps(1)}
            sx={style}
          />
          <Tab
            label={translate("input-dialog-raw-wood-tab-handling")}
            {...A11yProps(2)}
            sx={style}
          />
        </Tabs>
      );
    } else return null;
  };

  // --- Render --- //
  // Wrap setter to make them dirty when changed through UI
  const setCertificationDirty = makeDirtySetter(handleSetCertification);
  const setCertificationNumbersDirty = makeDirtySetter(setCertificationNumbers);
  const setCertificationClaimsDirty = makeDirtySetter(setCertificationClaims);
  const setOriginsDirty = makeDirtySetter(handleSetOrigins);
  const setResponsibleDirty = makeDirtySetter(handleSetResponsible);
  const setDateDirty = makeDirtySetter(setDate);
  const setAssortmentDirty = makeDirtySetter(setAssortment);
  const setAverageLogLengthDirty = makeDirtySetter(handleSetAverageLogLength);
  const setIsChippedDirty = makeDirtySetter(setIsChipped);
  const setAverageLogDiameterDirty = makeDirtySetter(handleSetAverageLogDiameter);
  const setNumberOfSectionsDirty = makeDirtySetter(setNumberOfSections);
  const setMeasureBothSidesDirty = makeDirtySetter(setMeasureBothSides);
  const setStackLengthFrontDirty = makeDirtySetter(setStackLengthFront);
  const setStackLengthBackDirty = makeDirtySetter(setStackLengthBack);
  const setFrontHeightsDirty = makeDirtySetter(setFrontHeights);
  const setBackHeightsDirty = makeDirtySetter(setBackHeights);
  const setStackingValueDirty = makeDirtySetter(setStackingValue);
  const setStraightnessValueDirty = makeDirtySetter(setStraightnessValue);
  const setProcessingValueDirty = makeDirtySetter(setProcessingValue);
  const setSnowIceValueDirty = makeDirtySetter(setSnowIceValue);
  const setResiduesValueDirty = makeDirtySetter(setResiduesValue);
  const setInputModeBattensDirty = makeDirtySetter(setInputModeBattens);
  const setInputModeRejectsDirty = makeDirtySetter(setInputModeRejects);
  const setWoodChipManualVolumeDirty = makeDirtySetter(handleSetWoodChipVolumeManuel);
  const setWoodChipManualCompFactorDirty = makeDirtySetter(setWoodChipManualCompFactor);
  const setWoodLogAssortmentDirty = makeDirtySetter(setWoodLogAssortment);
  const setSelectedLoggingCompanyStringDirty = makeDirtySetter(setSelectedLoggingCompanyString);
  const setSelectedForwardingCompanyStringDirty = makeDirtySetter(
    setSelectedForwardingCompanyString,
  );
  const setSelectedChippingCompanyStringDirty = makeDirtySetter(setSelectedChippingCompanyString);
  const setSelectedLoggingCompanyDirty = makeDirtySetter(setSelectedLoggingCompany);
  const setSelectedForwardingCompanyDirty = makeDirtySetter(setSelectedForwardingCompany);
  const setSelectedChippingCompanyDirty = makeDirtySetter(setSelectedChippingCompany);
  const setSelectedBuyerDirty = makeDirtySetter((value: typeof selectedBuyer) => {
    if (value[0]?.orgId !== selectedBuyer[0]?.orgId || value[0]?.id !== selectedBuyer[0]?.id) {
      setReleasedToBuyer(false);
      handleSetBuyerApprovalStatus("draft");
      setBuyerNote("");
    }
    setSelectedBuyer(value);
  });
  const setReleasedToBuyerDirty = handleReleasedToBuyerChange;
  const setSelectedBuyerApprovalStatusDirty = makeDirtySetter((value: BuyerStatusChangeProps["status"]) => {
    if (!canDecide) return;
    if (!releasedToBuyer) return;
    if (buyer?.id !== selectedBuyer[0]?.orgId || buyer?.userId !== selectedBuyer[0]?.id) return;
    if (value !== "approved" && value !== "rejected") return;
    handleSetBuyerApprovalStatus(value);
  });
  const setBuyerNoteDirty = makeDirtySetter(setBuyerNote);
  const setBuyerApprovalRequirementsDirty = makeDirtySetter(setBuyerApprovalRequirements);
  const setPriceDirty = makeDirtySetter(setPrice);
  const setSelectedStatusDirty = makeDirtySetter(setSelectedStatus);
  const setInventoryStatusNoteDirty = makeDirtySetter(setInventoryStatusNote);
  const setAccessibilityDirty = makeDirtySetter(setAccessibility);
  const setAccessibilityNoteDirty = makeDirtySetter(setAccessibilityNote);

  return (
    <>
      <SingleColumnInput
        open={open}
        title={title}
        tabs={tabs()}
        handleClose={handleCloseInput}
        handleSave={handleSave}
        buttonText={translate("save")}
        maxWidth="sm"
        loading={loading}
        saveDisabled={
          !canEditStack && (!canDecide || (savedApproval === "approved" && selectedBuyerApprovalStatus !== "rejected"))
        }
      >
        <RawWoodInput
          title={title}
          variant={variant}
          inventoryDataConfig={{ ...AppConfig.inventoryData, changeData: canEditStack ? "enabled" : "disabled", buyerApproval: canDecide ? "enabled" : "disabled" }}
          tabValue={tabValue}
          countryCode={countryCode}
          location={selectedInventoryItem.coordinates}
          inventoryId={selectedInventoryItem.id}
          uniqueInventoryId={uniqueInventoryId}
          inventoryDisplayReference={inventoryDisplayReference}
          certification={certification}
          certificationNumbers={certificationNumbers}
          certificationClaims={certificationClaims}
          certificationNumber={certificationNumber}
          originsOptions={originsOptions}
          origins={origins}
          responsibleOptions={responsibleOptions}
          responsible={responsible}
          date={date}
          initSpeciesArray={initSpeciesArray}
          speciesOptions={speciesOptions}
          assortmentOptions={assortmentOptions}
          assortment={assortment}
          averageLogLength={averageLogLength}
          averageLogDiameter={averageLogDiameter}
          averageStackHeight={averageStackHeight}
          numericMeasurements={numericMeasurements}
          averageStackLength={averageStackLength}
          woodChipManualVolume={woodChipManualVolume}
          woodChipManualCompFactor={woodChipManualCompFactor}
          woodLogAssortmentOptions={woodLogAssortmentOptions}
          woodLogAssortment={woodLogAssortment}
          isChipped={isChipped}
          woodLogSpeciesOptions={woodLogSpeciesOptions}
          woodLogClassOptions={woodLogClassOptions}
          woodLogClassExportOptions={woodLogClassExportOptions}
          initLogRows={initLogRows}
          measurements={{
            numberOfSections,
            numberOfSectionsBack,
            measureBothSides,
            stackLengthFront,
            stackLengthBack,
            frontHeights,
            backHeights,
            setNumberOfSections: setNumberOfSectionsDirty,
            setNumberOfSectionsBack: makeDirtySetter(setNumberOfSectionsBack),
            setMeasureBothSides: setMeasureBothSidesDirty,
            setStackLengthFront: setStackLengthFrontDirty,
            setStackLengthBack: setStackLengthBackDirty,
            setFrontHeights: setFrontHeightsDirty,
            setBackHeights: setBackHeightsDirty,
          }}
          stacking={{
            stackingOptions,
            straightnessOptions,
            processingOptions,
            snowIceOptions,
            residuesOptions,
            rejectsOptions,
            battensOptions,
            stackingValue,
            straightnessValue,
            processingValue,
            snowIceValue,
            residuesValue,
            rejectsValue,
            battensValue,
            inputModeBattens,
            inputModeRejects,
            setStackingValue: setStackingValueDirty,
            setStraightnessValue: setStraightnessValueDirty,
            setProcessingValue: setProcessingValueDirty,
            setSnowIceValue: setSnowIceValueDirty,
            setResiduesValue: setResiduesValueDirty,
            handleSetBattensAndRejectsValue,
            setInputModeBattens: setInputModeBattensDirty,
            setInputModeRejects: setInputModeRejectsDirty,
          }}
          handling={{
            companyOptions: handlingCompaniesOptions,
            buyerOptions: buyerCompanyOptions,
            buyerSelectionLocked,
            statusOptions,
            accessibilityOptions,
            buyerApprovalOptions,
            selectedBuyer,
            buyerApprovalRequirements,
            buyerApprovalRequirementsLoaded,
            selectedForwardingCompanyString,
            selectedLoggingCompanyString,
            selectedChippingCompanyString,
            selectedLoggingCompany,
            selectedForwardingCompany,
            selectedChippingCompany,
            selectedStatus,
            selectedBuyerApprovalStatus,
            price,
            inventoryStatusNote,
            accessibility,
            accessibilityNote,
            buyerNote,
            releasedToBuyer,
            setSelectedForwardingCompanyString: setSelectedForwardingCompanyStringDirty,
            setSelectedLoggingCompanyString: setSelectedLoggingCompanyStringDirty,
            setSelectedChippingCompanyString: setSelectedChippingCompanyStringDirty,
            setSelectedBuyer: setSelectedBuyerDirty,
            setSelectedLoggingCompany: setSelectedLoggingCompanyDirty,
            setSelectedForwardingCompany: setSelectedForwardingCompanyDirty,
            setSelectedChippingCompany: setSelectedChippingCompanyDirty,
            setSelectedStatus: setSelectedStatusDirty,
            setSelectedBuyerApprovalStatus: setSelectedBuyerApprovalStatusDirty,
            setPrice: setPriceDirty,
            setInventoryStatusNote: setInventoryStatusNoteDirty,
            setAccessibility: setAccessibilityDirty,
            setAccessibilityNote: setAccessibilityNoteDirty,
            setBuyerNote: setBuyerNoteDirty,
            setBuyerApprovalRequirements: setBuyerApprovalRequirementsDirty,
            setReleasedToBuyer: setReleasedToBuyerDirty,
            setDeliveryNoteGenerating: setLoading,
          }}
          setCertification={setCertificationDirty}
          setCertificationNumbers={setCertificationNumbersDirty}
          setCertificationClaims={setCertificationClaimsDirty}
          setOrigins={setOriginsDirty}
          setResponsible={setResponsibleDirty}
          setDate={setDateDirty}
          setAssortment={setAssortmentDirty}
          setAverageLogLength={setAverageLogLengthDirty}
          setIsChipped={setIsChippedDirty}
          setAverageLogDiameter={setAverageLogDiameterDirty}
          setWoodChipManualVolume={setWoodChipManualVolumeDirty}
          setWoodChipManualCompFactor={setWoodChipManualCompFactorDirty}
          setWoodLogAssortment={setWoodLogAssortmentDirty}
          saveFiles={saveFiles}
          speciesRef={speciesRef}
          logRowsRef={logRowsRef}
        />
      </SingleColumnInput>

      <ConfirmDialog
        open={openConfirmDialog}
        matchDownMD={true}
        invertConfirmActions={true}
        title={translate("loss-of-data-title")}
        text={translate("loss-of-data-text")}
        loading={loading}
        onConfirmText={translate("loss-of-data-cancel")}
        onConfirm={handleCancelConfirmDialog}
        onCancelText={translate("loss-of-data-proceed")}
        onCancel={handleConfirmDialog}
      />
    </>
  );
};

export default RawWoodInventoryAndMeasurementInput;
